// The kit's contents — its recipe, one authored row per item (ADR-018
// Decision 1: the portal owns the customer-authorable core). Add / remove /
// reorder over ONE array, which is why the Assembly control mounts once
// (ADR-018 Decision 2): two of these over the same `assembly.kit_contents` would
// be a double-write affordance, not a second view.
//
// Three origins, three different questions:
//   catalog_sku — an item Argo already stocks for this company, picked from the
//                 same catalog the fulfillment SKU picker offers.
//   quote_line  — a project on THIS order. The reference is portal-only: it is
//                 validated at the write boundary and STRIPPED at transmit, and
//                 what crosses is the resolved description + per-kit quantity
//                 (ADR-018 Decision 3). Only PERSISTED projects are offered — a
//                 synthetic local id is not a line id the backend can resolve,
//                 so a row claiming one is refused (400) at that boundary.
//   described   — a new item, in the customer's own words. The escape hatch
//                 every row has, and the origin a new row opens on in a kit
//                 build. A REWORK row opens on the stock picker instead — it
//                 names stock Argo already holds — falling back to `described`
//                 when the company's catalog is empty.
//
// `role` ("what's it for?") is a KIT-ONLY question and is not rendered in rework
// mode: a rework row is stock being worked on, not a component with a place in a
// kit. `supply_source` IS asked in both, because a rework can add or swap in an
// item Argo buys or produces.

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  COMPONENT_SUPPLY_SOURCES,
  COMPONENT_SUPPLY_SOURCE_LABELS,
  KIT_COMPONENT_ROLES,
  KIT_COMPONENT_ROLE_LABELS,
  KIT_CONTENTS_FIELD_KEY,
  KIT_CONTENTS_LINE_REF_FIELD_KEY,
  KIT_CONTENT_ORIGINS,
  KIT_CONTENT_ORIGIN_LABELS,
  kitContentRowIncompleteIn,
  kitContentRowNamesItem,
  type AssemblyMode,
  type CompanyItem,
  type KitComponentRole,
  type KitContentOrigin,
  type KitContentRow,
} from 'shared';

import { formatNumber } from '../i18n/formatters.js';
import { parseWholeNumberInput, WHOLE_NUMBER_INPUT_PROPS } from '../lib/wholeNumberInput.js';
import { RowDisclosure, RowRecap, useCollapsedRows } from './CollapsibleRows.js';
import { CompanyItemPicker } from './CompanyItemPicker.js';
import { MoveDownIcon, MoveUpIcon, RemoveIcon } from './ControlIcons.js';
import { fieldDomId, useFieldDomId, useFieldIdPrefix } from './fieldDomId.js';
import { FieldReadBack, readBackValue } from './FieldReadBack.js';
import { useCompanyStock } from './useCompanyStock.js';
import { localizeVocabLabels } from './vocabLabels.js';

// One project on this quote, as the "something on this order" picker offers it.
// The label is the same display name the project's own card shows, so the two
// read identically; it is frozen onto the row at pick time (the display-continuity
// posture bom_snapshot takes) so the row keeps reading correctly if the project
// is later renamed or removed.
export type KitContentLineOption = {
  id: string;
  label: string;
};

// Client-side row key. Portal-internal and never transmitted — React keys,
// reorder and per-row error reporting all hang off it. Exported because the
// rework picker's pre-fill mints rows too (KittingFields), and two generators
// would be two answers to "what is a row id here".
let rowSeq = 0;
export function newKitContentRowId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  rowSeq += 1;
  return `row-${Date.now()}-${rowSeq}`;
}

// Most kits are one main item plus things that go with it, so the first row
// offered is the main item and every later one an insert. Both are ordinary
// defaults the customer overrides in the row's own "What's it for?" picker.
function defaultRole(rows: readonly KitContentRow[]): KitComponentRole {
  return rows.some((r) => r.role === 'primary') ? 'insert' : 'primary';
}

// ONE kit row's stock picker — the shared CompanyItemPicker with this row's label
// around it. The same control, reading the same feed, as the rework picker that
// asks which kit or SKU the job is working on: one question about a Company's
// stock, one control, one query.
function CatalogSkuPicker({
  id,
  value,
  storedLabel,
  invalid,
  describedBy,
  onPick,
}: {
  id: string;
  value: string;
  // What the row froze at pick time. Keeps a stored pick readable when the
  // catalog no longer lists it — see CompanyItemPicker.
  storedLabel?: string | undefined;
  invalid?: boolean;
  describedBy?: string;
  onPick: (item: CompanyItem | null) => void;
}): JSX.Element {
  const { t } = useTranslation('quote');
  return (
    <div className="form-row">
      <label htmlFor={id}>{t('orderServices.assembly.contents.skuLabel')}</label>
      <CompanyItemPicker
        id={id}
        value={value}
        storedLabel={storedLabel}
        emptyOptionLabel={t('orderServices.assembly.contents.skuEmptyOption')}
        emptyCatalogLabel={t('orderServices.assembly.contents.skuCatalogEmpty')}
        onPick={onPick}
        {...(invalid !== undefined ? { invalid } : {})}
        {...(describedBy !== undefined ? { describedBy } : {})}
      />
    </div>
  );
}

type KitContentsEditorProps = {
  rows: readonly KitContentRow[];
  onChange: (rows: KitContentRow[]) => void;
  lines: readonly KitContentLineOption[];
  // True when the quote carries projects that are not persisted yet, so the
  // picker above cannot offer them.
  hasUnsavedLines?: boolean;
  // Asked for when a row is added: adding the next item is the customer saying
  // the ones before it stand, and the rows are snapshot state a reload otherwise
  // drops. The caller persists the quote as it stands — the new row itself is not
  // persistable yet (no item, no description, so normalizeKitContentRow refuses
  // it), which is why the caller's save has to preserve local state rather than
  // adopt what comes back.
  onRequestSave?: (() => void) | undefined;
  errors: Record<string, string>;
  // Renders every row as its ANSWERS rather than as a dead editor: the pickers
  // become static read-backs (a <select> has no `readOnly`, and a disabled one
  // takes its answer out of the tab order with the focus) and the text fields
  // become `readOnly` and locked-looking. The reorder / remove / add controls are
  // HIDDEN — an action carries no answer to read back, so there is nothing for a
  // dead button to state. The only surface that passes this is the read-only
  // Assembly panel on a submitted order, which asks nothing, so it is the same
  // key EnumRadios / EnumChecks read.
  disabled?: boolean;
  // Which question this list is answering: what goes INTO a kit, or which stock
  // is being reworked (ADR-018 Decision 7). One list, two voices — the rows are
  // the same shape, and every correctness property they already carry (the
  // portal-only line reference, the clone remap, the write-boundary scope check)
  // is the same in both.
  mode: AssemblyMode;
  // The heading level this list's title renders at. The Assembly panel sits at
  // different depths on its two mounts — under a subsection h4 when editable,
  // directly under the read-only card's h3 when not — so the level is the
  // caller's to say (see AssemblyPanel). Defaults to the editable path's.
  headingLevel?: 'h4' | 'h5';
};

export function KitContentsEditor({
  rows,
  onChange,
  lines,
  hasUnsavedLines = false,
  onRequestSave,
  errors,
  disabled,
  mode,
  headingLevel: Heading = 'h5',
}: KitContentsEditorProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const isRework = mode === 'rework';
  const isPerUnit = mode === 'per_unit';
  const listDomId = useFieldDomId(KIT_CONTENTS_FIELD_KEY);
  const idPrefix = useFieldIdPrefix();
  // Canonical vocabulary labels resolved through the catalog — see
  // localizeVocabLabels. The roles and supply sources are Nexus's key sets
  // mirrored verbatim (ADR-018 Decision 1), so the VALUES stay canonical and
  // only the display string is translated.
  const roleLabels = localizeVocabLabels(
    'orderServices.assembly.contents.roleOptions',
    KIT_COMPONENT_ROLE_LABELS,
    t
  );
  const supplySourceLabels = localizeVocabLabels(
    'orderServices.assembly.contents.supplyOptions',
    COMPONENT_SUPPLY_SOURCE_LABELS,
    t
  );
  const originLabels = localizeVocabLabels<KitContentOrigin>(
    'orderServices.assembly.contents.originOptions',
    KIT_CONTENT_ORIGIN_LABELS,
    t
  );

  // The partner's own product catalogue — their ITEM MASTER, not the
  // finished-goods list Argo warehouses for them. The row picker offers it beside
  // Argo's stock (CatalogSkuPicker); the probe below asks only whether the
  // partner has stock of their OWN (useCompanyStock). A kit component is "one of
  // my products", which is a wider question than "something Argo already fulfils
  // for me": the fulfillment list is keyed on ownership alone and excludes
  // anything without a live stock relationship, so a product a partner owns and
  // has never had Argo warehouse was unpickable here. The fulfillment list keeps
  // its own home, on the fulfillment section's finished-goods question.
  //
  // Already sorted by name at the data layer (Nexus orders it) — R25, so nothing
  // re-sorts here. Non-price company data, so caching is permitted (R4).
  //
  // Whether this Company has stock of its own — which decides only what a
  // new rework row opens on. The "something you already stock" origin is offered
  // either way: an empty catalogue is shown to the customer inside the picker
  // (CompanyItemPicker's empty-catalog line), never hidden by dropping the
  // option, because a missing option reads as a missing feature. Not the
  // picker's list; see useCompanyStock.
  const hasOwnStock = useCompanyStock({ enabled: !disabled });

  // `quote_line` is not offered on an order with no projects to point at, nor in
  // per_unit, whose rows are units of stock already held. `catalog_sku` always is,
  // empty catalogue or not (see above), and `described` always is — the escape
  // hatch every axis needs. A row's OWN origin is always offered too: the catalog
  // query is skipped on a read-only quote and a project can be removed mid-edit,
  // and a <select> whose value matches no option renders blank, reading as "your
  // pick is gone" when it is only unlisted this render.
  const offerableOrigins = useMemo(
    () =>
      KIT_CONTENT_ORIGINS.filter((o) =>
        o === 'quote_line' ? !isPerUnit && lines.length > 0 : true,
      ),
    [lines.length, isPerUnit],
  );
  const originsFor = (row: KitContentRow): readonly KitContentOrigin[] =>
    offerableOrigins.includes(row.origin)
      ? offerableOrigins
      : KIT_CONTENT_ORIGINS.filter((o) => o === row.origin || offerableOrigins.includes(o));

  // What a `quote_line` row's picker was showing, for the read-back that replaces
  // it: the LIVE project name where the project is still offered, so a rename the
  // picker would have reflected is reflected here too, and the description frozen
  // at pick time when it is not — the same two-step the picker itself makes with
  // its unlisted-pick option.
  const lineLabelFor = (row: KitContentRow): string | undefined =>
    lines.find((l) => l.id === row.line_id)?.label ?? row.description ?? row.line_id;

  const listError = errors[KIT_CONTENTS_FIELD_KEY];
  const lineRefError = errors[KIT_CONTENTS_LINE_REF_FIELD_KEY];

  function patchRow(rowId: string, patch: Partial<KitContentRow>): void {
    onChange(rows.map((r) => (r.row_id === rowId ? { ...r, ...patch } : r)));
  }

  const addRef = useRef<HTMLButtonElement>(null);

  // Which rows are folded to their one-line recap. A twenty-item kit is twenty
  // full fieldsets, so the list scrolls away from the customer as they build it —
  // the same problem the quote's item list answers by folding an item once the
  // next one is added. View state only: a folded row stays mounted, validated
  // and transmitted exactly as it was.
  const collapse = useCollapsedRows(rows.map((r) => r.row_id));

  function addRow(): void {
    // A rework row names stock Argo already holds, so the stock picker is the
    // right first question there; a kit's commonest row by far is a project on
    // THIS order, so that picker opens first. Neither opens on an origin with
    // nothing to pick from — with no catalog, or no projects, the row falls back
    // to the escape hatch every row has.
    const preferred: KitContentOrigin = isRework || isPerUnit ? 'catalog_sku' : 'quote_line';
    const preferredFillable =
      preferred === 'catalog_sku' ? hasOwnStock : offerableOrigins.includes(preferred);
    const origin: KitContentOrigin = preferredFillable ? preferred : 'described';
    // Adding the next item says the ones before it are named, so they fold away
    // and the new row opens against a short list. A row still missing its item or
    // its quantity stays open — folding it would hide the field being asked for —
    // and the predicate deciding that is the shared one the save drops the row on
    // (R12).
    collapse.collapseFinished(
      rows.filter((r) => !kitContentRowIncompleteIn(mode, r)).map((r) => r.row_id),
    );
    onChange([
      ...rows,
      {
        row_id: newKitContentRowId(),
        origin,
        role: defaultRole(rows),
        supply_source: 'partner_supplied',
        quantity_per_kit: 1,
      },
    ]);
    // Batched with the row write above, so the caller's save runs against a
    // committed snapshot that already carries the new row.
    onRequestSave?.();
  }

  function removeRow(rowId: string): void {
    onChange(rows.filter((r) => r.row_id !== rowId));
    collapse.drop(rowId);
    // Same reason as the recipe's step removal: the control unmounts with its row and
    // focus falls to <body>, so a keyboard customer deleting a middle row loses
    // their place in the item list. The add button is always mounted alongside a
    // remove button — the read-only panel hides both — so it is always a target.
    addRef.current?.focus();
  }

  function moveRow(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
  }

  // Switching origin clears the identifier the OLD origin owned, so a
  // half-finished SKU pick can never ride along on a line reference (the same
  // move FulfillmentFields makes between its existing/new SKU branches). The
  // frozen description is kept — it is what the customer last said the item is,
  // and it is the display text every origin shares.
  function pickOrigin(row: KitContentRow, origin: KitContentOrigin): void {
    patchRow(row.row_id, { origin, sku: undefined, line_id: undefined });
  }

  // Picking a project freezes its display name onto the row and records that
  // Argo produces it — a project on this order is printed here by definition, so
  // that is a fact about the row, not a guess at the customer's intent.
  function pickLine(row: KitContentRow, lineId: string): void {
    if (lineId === row.line_id) return;
    const line = lines.find((l) => l.id === lineId);
    if (!line) {
      patchRow(row.row_id, { line_id: undefined });
      return;
    }
    patchRow(row.row_id, {
      line_id: line.id,
      description: line.label,
      supply_source: 'argo_produced',
    });
  }

  // `item_id` is the selection key, not necessarily a SKU: Nexus answers
  // `sku || id`, so a product with no WMS SKU still picks. It lands in the row's
  // `sku` field, which is the same opaque-handle shape `bom_id` already carries.
  // `uom` answers the row's unit, so the customer does not retype "each" under a
  // product that already states its unit.
  //
  // An Argo stock pick records that Argo supplies it, the same kind of fact
  // pickLine records: Argo's own carton is not something the partner sends in,
  // and leaving the row on "I'll send it to you" would tell Nexus the customer
  // supplies the kit's components. Moving OFF an Argo pick — to the partner's own
  // item, or to no pick — puts the row back on the new-row default, so the
  // picker never leaves behind a supply answer it wrote for an item no longer
  // chosen.
  function pickSku(row: KitContentRow, picked: CompanyItem | null): void {
    if (picked?.item_id === row.sku) return;
    const supply_source: KitContentRow['supply_source'] = picked?.is_argo_stock
      ? 'argo_purchased'
      : row.supply_source === 'argo_purchased'
        ? 'partner_supplied'
        : row.supply_source;
    if (!picked) {
      patchRow(row.row_id, { sku: undefined, supply_source });
      return;
    }
    patchRow(row.row_id, {
      sku: picked.item_id,
      description: picked.name,
      ...(picked.uom ? { unit: picked.uom } : {}),
      supply_source,
    });
  }

  return (
    <div
      className={`kit-contents${listError ? ' kit-contents--error' : ''}`}
      id={listDomId}
    >
      <Heading className="kit-contents__title">
        {isPerUnit
          ? t('orderServices.assembly.perUnit.contentsTitle')
          : isRework
            ? t('orderServices.assembly.rework.contentsTitle')
            : t('orderServices.assembly.contents.title')}
        {/* Optional in a kit build and a rework — a kit may be described in the instructions
            box and a rework in its steps (assemblyMissingFieldKeys only ever
            requires that a row which EXISTS be finished). Said out loud with the
            same suffix its sibling list carries, so the two section headers do
            not disagree about whether they are answering a required question. */}
        {isPerUnit ? (
          !disabled && <span className="required-marker"> *</span>
        ) : (
          <span className="help"> {t('shipTo.fields.optionalSuffix')}</span>
        )}
      </Heading>
      <p className="help help--flush">
        {isPerUnit
          ? t('orderServices.assembly.perUnit.contentsHelp')
          : isRework
            ? t('orderServices.assembly.rework.contentsHelp')
            : t('orderServices.assembly.contents.help')}
      </p>

      {/* The empty prompt names the Add button ("add an item, or describe the kit
          below") and both are gone on a read-only mount, so it reads back as the
          same em-dash a blank column in a row does — a list nobody filled in is
          an answer, and dropping the line entirely would leave the reader unable
          to tell the question was asked. */}
      {rows.length === 0 &&
        (disabled ? (
          <p className="value--locked">{readBackValue(undefined)}</p>
        ) : (
          <p className="kit-contents__empty">
            {isPerUnit
              ? t('orderServices.assembly.perUnit.contentsEmpty')
              : isRework
                ? t('orderServices.assembly.rework.contentsEmpty')
                : t('orderServices.assembly.contents.empty')}
          </p>
        ))}
      {hasUnsavedLines && lines.length === 0 && (
        <p className="help help--flush">{t('orderServices.assembly.contents.lineUnsavedNote')}</p>
      )}

      {rows.map((row, idx) => {
        const n = idx + 1;
        // Every affordance `incomplete` drives — the outlined row, the aria-invalid
        // on the identity field, the hint below it — asks for a repair: name the
        // item, give it a quantity, or remove the row. A read-only mount offers
        // none of those, so on a submitted order the row simply reports what it
        // holds and the missing part reads back as the em-dash it is. Reachable
        // only through legacy data: an incomplete row is dropped on save.
        const incomplete = !disabled && kitContentRowIncompleteIn(mode, row);
        // WHICH miss, so the hint and the aria-invalid land on the field that is
        // actually empty. A row that names nothing and a row with no quantity are
        // both dropped on save (kitContentRowIncomplete), but they read as
        // different mistakes to the customer, so they cannot share one message.
        // Asked of the SAME predicate the validator uses, or a row whose cleared
        // SKU still carries the name that pick wrote would be told to name an item
        // it has already named.
        const missingIdentity = incomplete && !kitContentRowNamesItem(row);
        // Every id this row renders hangs off one namespaced base, so a second
        // Assembly panel on the page (an `assembly` item's own kit) cannot
        // duplicate a row id into the document — see fieldDomId.
        const rowKey = fieldDomId(idPrefix, `kit_contents.${row.row_id}`);
        const hintId = `${rowKey}.hint`;
        const bodyId = `${rowKey}.body`;
        const folded = !disabled && collapse.isCollapsed(row.row_id);
        // What the row IS, read the way the row's own fields read it: the live
        // project name for a project reference, the description frozen at pick
        // time otherwise.
        const recapText =
          (row.origin === 'quote_line' ? lineLabelFor(row) : row.description?.trim()) ||
          t('orderServices.assembly.collapse.itemUnnamed');
        return (
          <fieldset
            key={row.row_id}
            className={`kit-row${incomplete ? ' kit-row--incomplete' : ''}${
              folded ? ' kit-row--collapsed' : ''
            }`}
          >
            <legend className="kit-row__legend">
              {/* No chevron on a read-only mount: nothing is being entered for a
                  fold to get out of the way of, and a folded row would be an
                  answer the reader has to go find. Same reason the reorder and
                  remove cluster goes. */}
              {!disabled && (
                <RowDisclosure
                  collapsed={folded}
                  bodyId={bodyId}
                  label={
                    folded
                      ? t('orderServices.assembly.collapse.expandItemAria', { n })
                      : t('orderServices.assembly.collapse.collapseItemAria', { n })
                  }
                  onToggle={() => collapse.toggle(row.row_id)}
                />
              )}
              {t('orderServices.assembly.contents.rowLegend', { n })}
            </legend>
            {/* The whole cluster goes, not three disabled buttons: the row's
                position and its presence are already what the list reads back,
                so the controls that would change them state nothing a submitted
                order needs said. The legend above still numbers the row. */}
            {!disabled && (
              <div className="kit-row__actions">
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('orderServices.assembly.contents.moveUp', { n })}
                  onClick={() => moveRow(idx, -1)}
                  disabled={idx === 0}
                >
                  <MoveUpIcon />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('orderServices.assembly.contents.moveDown', { n })}
                  onClick={() => moveRow(idx, 1)}
                  disabled={idx === rows.length - 1}
                >
                  <MoveDownIcon />
                </button>
                <button
                  type="button"
                  className="icon-button icon-button--danger"
                  aria-label={t('orderServices.assembly.contents.remove', { n })}
                  onClick={() => removeRow(row.row_id)}
                >
                  <RemoveIcon />
                </button>
              </div>
            )}

            {/* What the row IS, then the quantity that tells it from its siblings
                — the two facts a folded item has to carry. */}
            {folded && (
              <RowRecap
                text={recapText}
                bodyId={bodyId}
                editLabel={t('orderServices.assembly.collapse.edit')}
                onExpand={() => collapse.toggle(row.row_id)}
                meta={
                  typeof row.quantity_per_kit === 'number'
                    ? row.unit?.trim()
                      ? t('orderServices.assembly.collapse.qtyWithUnit', {
                          quantity: formatNumber(row.quantity_per_kit),
                          unit: row.unit.trim(),
                        })
                      : t('orderServices.assembly.collapse.qty', {
                          quantity: formatNumber(row.quantity_per_kit),
                        })
                    : undefined
                }
                flag={incomplete ? t('orderServices.assembly.collapse.needsAttention') : undefined}
              />
            )}

            {/* Hidden, never unmounted: what the customer typed and each picker's
                own state survive a fold, and `hidden` takes the subtree out of the
                tab order and the accessibility tree as well — the posture the item
                list takes for the same reasons. */}
            <div id={bodyId} hidden={folded}>
              {disabled ? (
                <FieldReadBack
                  id={`${rowKey}.origin`}
                  label={t('orderServices.assembly.contents.originLabel')}
                  value={originLabels[row.origin]}
                />
              ) : (
                <div className="form-row">
                  <label htmlFor={`${rowKey}.origin`}>
                    {t('orderServices.assembly.contents.originLabel')}
                  </label>
                  <select
                    id={`${rowKey}.origin`}
                    value={row.origin}
                    onChange={(e) => pickOrigin(row, e.target.value as KitContentOrigin)}
                  >
                    {originsFor(row).map((o) => (
                      <option key={o} value={o}>
                        {originLabels[o]}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {row.origin === 'quote_line' &&
                (disabled ? (
                  <FieldReadBack
                    id={`${rowKey}.line_id`}
                    label={t('orderServices.assembly.contents.lineLabel')}
                    value={lineLabelFor(row)}
                  />
                ) : (
                  <div className="form-row">
                    <label htmlFor={`${rowKey}.line_id`}>
                      {t('orderServices.assembly.contents.lineLabel')}
                    </label>
                    <select
                      id={`${rowKey}.line_id`}
                      value={row.line_id ?? ''}
                      onChange={(e) => pickLine(row, e.target.value)}
                      aria-required="true"
                      aria-invalid={missingIdentity ? true : undefined}
                      aria-describedby={incomplete ? hintId : undefined}
                    >
                      <option value="">{t('orderServices.assembly.contents.lineEmptyOption')}</option>
                      {lines.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.label}
                        </option>
                      ))}
                      {row.line_id && !lines.some((l) => l.id === row.line_id) && (
                        <option value={row.line_id}>
                          {row.description ?? row.line_id}
                        </option>
                      )}
                    </select>
                  </div>
                ))}

              {row.origin === 'catalog_sku' &&
                (disabled ? (
                  // The catalog query is switched off here, so the picker's list is
                  // EMPTY and the pick's own fallback entry is the only thing left
                  // in it — the frozen description under another name. Reading the
                  // description directly says the same thing without a dead control.
                  <FieldReadBack
                    id={`${rowKey}.sku`}
                    label={t('orderServices.assembly.contents.skuLabel')}
                    value={row.description ?? row.sku}
                  />
                ) : (
                  <CatalogSkuPicker
                    id={`${rowKey}.sku`}
                    value={row.sku ?? ''}
                    storedLabel={row.description}
                    invalid={missingIdentity}
                    onPick={(picked) => pickSku(row, picked)}
                    {...(incomplete ? { describedBy: hintId } : {})}
                  />
                ))}

              <div className="form-row">
                <label htmlFor={`${rowKey}.description`}>
                  {t('orderServices.assembly.contents.descriptionLabel')}
                  {/* No marker on a read-back: it would promise a gate that has
                      nothing left to report. */}
                  {row.origin === 'described' && !disabled && (
                    <span className="required-marker"> *</span>
                  )}
                </label>
                {/* A described row with no description is the other half of
                    kitContentRowIncomplete, so it points at the same hint the
                    quantity does rather than failing silently. The example lives IN
                    the input — a per-row sentence beside it repeated once for every
                    item in the kit. A textarea sized to its content, not a one-line
                    input: a description names several parts often enough that a
                    single line hides most of what is being edited. */}
                <textarea
                  id={`${rowKey}.description`}
                  rows={1}
                  {...(disabled
                    ? {
                        readOnly: true,
                        'aria-readonly': true,
                        className: 'kit-row__description input--locked',
                      }
                    : {
                        className: 'kit-row__description',
                        placeholder: t('orderServices.assembly.contents.descriptionPlaceholder'),
                        'aria-required': row.origin === 'described' ? 'true' : undefined,
                      })}
                  value={disabled ? readBackValue(row.description) : (row.description ?? '')}
                  onChange={(e) => patchRow(row.row_id, { description: e.target.value })}
                  aria-invalid={missingIdentity && row.origin === 'described' ? true : undefined}
                  aria-describedby={incomplete ? hintId : undefined}
                />
              </div>

              <div className="kit-row__grid">
                {/* Kit-only: a rework row is stock being worked on, not a component
                    with a place in a kit, so "what's it for?" has no reading there.
                    `role` is not part of a row's completeness at all — every origin's
                    identity and its quantity are what kitContentRowIncomplete reads —
                    so defaultRole's value simply stands and transmits. */}
                {!isRework && !isPerUnit &&
                  (disabled ? (
                    <FieldReadBack
                      id={`${rowKey}.role`}
                      label={t('orderServices.assembly.contents.roleLabel')}
                      value={roleLabels[row.role]}
                    />
                  ) : (
                    <div className="form-row">
                      <label htmlFor={`${rowKey}.role`}>
                        {t('orderServices.assembly.contents.roleLabel')}
                      </label>
                      <select
                        id={`${rowKey}.role`}
                        value={row.role}
                        onChange={(e) =>
                          patchRow(row.row_id, { role: e.target.value as KitComponentRole })
                        }
                      >
                        {/* Nexus's own KitComponentRole order, mirrored verbatim: the
                            main item leads, the escape hatch trails. R25's alphabetical
                            convention governs data-layer catalogs like the SKU and
                            project pickers above, not a mirrored key set whose order
                            carries meaning. */}
                        {KIT_COMPONENT_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {roleLabels[r]}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                {disabled ? (
                  <FieldReadBack
                    id={`${rowKey}.supply_source`}
                    label={t('orderServices.assembly.contents.supplyLabel')}
                    value={supplySourceLabels[row.supply_source]}
                  />
                ) : (
                  <div className="form-row">
                    <label htmlFor={`${rowKey}.supply_source`}>
                      {t('orderServices.assembly.contents.supplyLabel')}
                    </label>
                    <select
                      id={`${rowKey}.supply_source`}
                      value={row.supply_source}
                      onChange={(e) =>
                        patchRow(row.row_id, {
                          supply_source: e.target.value as KitContentRow['supply_source'],
                        })
                      }
                    >
                      {COMPONENT_SUPPLY_SOURCES.map((s) => (
                        <option key={s} value={s}>
                          {supplySourceLabels[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-row">
                  <label htmlFor={`${rowKey}.quantity_per_kit`}>
                    {isRework || isPerUnit
                      ? t('orderServices.assembly.rework.quantityPerRowLabel')
                      : t('orderServices.assembly.contents.quantityLabel')}
                    {!disabled && <span className="required-marker"> *</span>}
                  </label>
                  {/* A locked number reads as TEXT, the way the derived counts above
                      this list already do: a readOnly number input still draws its
                      spinners in some browsers, offering a nudge that does nothing,
                      and the thousands separator is what the customer typed. */}
                  <input
                    id={`${rowKey}.quantity_per_kit`}
                    {...(disabled
                      ? {
                          type: 'text',
                          readOnly: true,
                          'aria-readonly': true,
                          className: 'input-compact input--locked',
                          value: readBackValue(
                            typeof row.quantity_per_kit === 'number'
                              ? formatNumber(row.quantity_per_kit)
                              : undefined,
                          ),
                        }
                      : {
                          ...WHOLE_NUMBER_INPUT_PROPS,
                          className: 'input-compact',
                          'aria-required': 'true',
                          value:
                            typeof row.quantity_per_kit === 'number' ? row.quantity_per_kit : '',
                        })}
                    onChange={(e) => {
                      const n = parseWholeNumberInput(e.target.value);
                      if (n === undefined) return;
                      patchRow(row.row_id, { quantity_per_kit: n ?? undefined });
                    }}
                    aria-invalid={incomplete ? true : undefined}
                    aria-describedby={incomplete ? hintId : undefined}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor={`${rowKey}.unit`}>
                    {t('orderServices.assembly.contents.unitLabel')}
                  </label>
                  <input
                    id={`${rowKey}.unit`}
                    type="text"
                    {...(disabled
                      ? {
                          readOnly: true,
                          'aria-readonly': true,
                          className: 'input-compact input--locked',
                        }
                      : {
                          className: 'input-compact',
                          placeholder: t('orderServices.assembly.contents.unitPlaceholder'),
                        })}
                    value={disabled ? readBackValue(row.unit) : (row.unit ?? '')}
                    onChange={(e) => patchRow(row.row_id, { unit: e.target.value })}
                  />
                </div>
              </div>

              {incomplete && (
                <p className="kit-row__hint" id={hintId}>
                  {missingIdentity
                    ? t('orderServices.assembly.contents.incompleteItem')
                    : isPerUnit
                      ? t('orderServices.assembly.perUnit.rowIncomplete')
                      : isRework
                        ? t('orderServices.assembly.rework.rowIncomplete')
                        : t('orderServices.assembly.contents.incomplete')}
                </p>
              )}
            </div>
          </fieldset>
        );
      })}

      {/* Above the Add button, not below it. The field-summary link scrolls to this
          container, and a message under the last control is a message the customer
          scrolls past on their way in. */}
      {listError && <span className="field-error">{listError}</span>}
      {lineRefError && <span className="field-error">{lineRefError}</span>}

      {!disabled && (
        <button type="button" ref={addRef} onClick={addRow}>
          {rows.length === 0
            ? t('orderServices.assembly.contents.addFirst')
            : t('orderServices.assembly.contents.addAnother')}
        </button>
      )}
    </div>
  );
}
